import { useDeepDiveContext } from "../DeepDiveProvider";
import { CALL_PROGRESS } from "../constants";
import { useEffect, useMemo, useRef } from "react";
import WaitingScreen from "./entryLayouts/WaitingScreen";
import ErrorScreen from "./entryLayouts/ErrorScreen";
import GetUserInfoScreen from "./entryLayouts/GetUserInfoScreen";
import ThankYouScreen from "./entryLayouts/ThankYouScreen";
import { useParams, useSearchParams } from "react-router-dom";
import CallScreen from "./entryLayouts/CallScreen";
import LanguageSelectionScreen from "./entryLayouts/LanguageSelectionScreen";

export default function Entry() {
  const { callProgress, verifySession, setPinnedStreams } = useDeepDiveContext();

  const { joiningKey } = useParams();
  const [searchParams] = useSearchParams();
  const rolePrefix = joiningKey?.[0];
  const isRecorder = rolePrefix === "r";
  const pinnedStreamsParams = searchParams.get("pinnedStreams");

  const hasFetched = useRef(false);

  const toVerifySession = isRecorder && joiningKey && callProgress === CALL_PROGRESS.WAITING_SCREEN;

  useEffect(() => {
    const sanitizeUrl = () => {
      const url = new URL(window.location.href);

      // List of known Airbridge-related query params
      const paramsToRemove = ["airbridge_referrer", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

      let changed = false;
      paramsToRemove.forEach((param) => {
        if (url.searchParams.has(param)) {
          url.searchParams.delete(param);
          changed = true;
        }
      });

      // If any params were removed, update the URL without reloading
      if (changed) {
        const cleanedUrl = url.pathname + url.search + url.hash;
        window.history.replaceState({}, "", cleanedUrl);
      }
    };

    window.addEventListener("load", sanitizeUrl);

    return () => {
      window.removeEventListener("load", sanitizeUrl);
    };
  }, []);

  useEffect(() => {
    if (toVerifySession) {
      if (hasFetched.current) return;
      hasFetched.current = true;

      verifySession(joiningKey || "", true);

      if (pinnedStreamsParams) {
        const ps = pinnedStreamsParams?.split(",")?.map((s) => Number(s));
        setPinnedStreams(ps);
      }
    }
  }, [callProgress, isRecorder, joiningKey, pinnedStreamsParams, setPinnedStreams, toVerifySession, verifySession]);

  const content = useMemo(() => {
    if (toVerifySession) return null;

    switch (callProgress) {
      case CALL_PROGRESS.LANGUAGE_SELECTION_SCREEN:
        return <LanguageSelectionScreen />;
      case CALL_PROGRESS.WAITING_SCREEN:
        return <WaitingScreen />;
      case CALL_PROGRESS.ERROR_SCREEN:
        return <ErrorScreen />;
      case CALL_PROGRESS.GET_USER_INFO_SCREEN:
        return <GetUserInfoScreen />;
      case CALL_PROGRESS.IN_CALL_SCREEN:
        return <CallScreen />;
      case CALL_PROGRESS.THANK_YOU_SCREEN:
        return <ThankYouScreen />;
      default:
        return null;
    }
  }, [callProgress, toVerifySession]);

  return <div className='entry'>{content}</div>;
}
